"use client";

import * as React from "react";
import {
  IconBuildingStore,
  IconLoader2,
  IconSearch,
  IconTrash,
} from "@tabler/icons-react";
import { toast } from "sonner";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ConnectAgencyClientDialog } from "@/components/connect-agency-client-dialog";
import { DisconnectAgencyClientDialog } from "@/components/disconnect-agency-client-dialog";
import {
  useAgencyClients,
  useConnectAgencyClient,
  useDisconnectAgencyClient,
  useClearLinkableClients,
  useLinkableClients,
} from "@/hooks/use-agencies";
import { isApiError } from "@/lib/axios";
import type { AgencyClient, LinkableClient } from "@/types";

const PAGE_SIZE = 10;
const SEARCH_DEBOUNCE_MS = 500;
/** One popover's worth of results; the admin narrows by typing, not by paging. */
const SEARCH_RESULT_LIMIT = 10;

/**
 * How long to leave a row in the "being removed" state before handing the
 * control back.
 *
 * The disconnect is asynchronous and there IS NO STATUS FIELD to read — the
 * row vanishing is the only completion signal the backend gives, and a failed
 * job is indistinguishable from a slow one. So the story's "if it fails the
 * row comes back enabled" has to be a ceiling on the wait. Generous, because
 * expiring early on a job that is merely slow would invite a second delete
 * while the first is still running.
 */
const REMOVAL_TIMEOUT_MS = 2 * 60 * 1000;

const formatDate = (dateString: string): string =>
  new Date(dateString).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });

/** Up to two initials, for the avatar when a venue has no logo. */
const getInitials = (name: string): string =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0]?.toUpperCase() ?? "")
    .join("") || "?";

/**
 * City and state are separate columns, and either can be absent — the backend
 * could not confirm they are carried on the venue at all, and they come from
 * an Address that is itself optional. One helper so both columns fall back
 * the same way instead of one of them rendering an empty cell.
 */
const displayOrDash = (value?: string | null): string => {
  const trimmed = value?.trim();
  return trimmed ? trimmed : "-";
};

/**
 * Compose operator-readable copy for a failed agency-client call.
 *
 * The connect endpoint's refusals are NAMED AND DISTINCT — 404 unknown venue,
 * 400 venue without marketing, 409 already connected — and the story requires
 * that the reason be shown rather than swallowed into one generic failure. The
 * server sends a sentence for each, so this prefers that sentence and only
 * falls back where there is nothing usable.
 */
function resolveAgencyClientError(error: unknown, fallback: string): string {
  if (isApiError(error)) {
    /**
     * Narrowed, not just optional-chained. `message` is TYPED `string |
     * undefined`, but the wire is a Nest error body, which sends an ARRAY for
     * validation errors — and `.trim()` on an array throws, inside the handler
     * whose whole job is to explain a failure. `resolveDeleteErrorMessage` in
     * the agencies table guards it the same way.
     */
    const detail =
      typeof error.data?.message === "string" ? error.data.message.trim() : "";
    return detail || fallback;
  }
  if (error instanceof Error && error.message) return error.message;
  return fallback;
}

/** A disconnect the browser has fired and is now waiting out. */
interface PendingRemoval {
  startedAt: number;
  name: string;
}

interface AgencyClientsTableProps {
  agencyId: string;
  agencyName: string;
  /**
   * Lifted so the detail page can show the client count alongside the people
   * count without issuing a second read for it.
   */
  onTotalChange?: (total: number) => void;
}

/**
 * The venues an agency works for, with the picker that connects a new one.
 *
 * Built to read as one screen with the people table above it — same density,
 * same pagination treatment, same empty-state shape — rather than as a second
 * table bolted underneath. Both dialogs are embedded here, as the venues table
 * embeds its own.
 */
export function AgencyClientsTable({
  agencyId,
  agencyName,
  onTotalChange,
}: AgencyClientsTableProps) {
  const [pageIndex, setPageIndex] = React.useState(0);

  // Picker state. `searchQuery` keeps the input responsive; `debouncedSearch`
  // is what actually reaches the API.
  const [pickerOpen, setPickerOpen] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState("");
  const [debouncedSearch, setDebouncedSearch] = React.useState("");

  const [pendingConnect, setPendingConnect] =
    React.useState<LinkableClient | null>(null);
  const [isConnectDialogOpen, setIsConnectDialogOpen] = React.useState(false);

  const [pendingDisconnect, setPendingDisconnect] =
    React.useState<AgencyClient | null>(null);
  const [isDisconnectDialogOpen, setIsDisconnectDialogOpen] =
    React.useState(false);

  // venueId → the removal we are waiting out. Non-empty turns polling on.
  const [removing, setRemoving] = React.useState<
    Record<string, PendingRemoval>
  >({});
  const isRemovingAny = Object.keys(removing).length > 0;

  const {
    data: clientsData,
    dataUpdatedAt,
    errorUpdatedAt,
    isLoading: isLoadingClients,
    error: clientsError,
  } = useAgencyClients(
    agencyId,
    { pageNumber: pageIndex + 1, pageSize: PAGE_SIZE }, // API uses 1-based page numbers
    isRemovingAny,
  );

  const clients = clientsData?.data ?? [];
  const totalClients = clientsData?.count ?? 0;

  const {
    data: linkableData,
    isLoading: isLoadingLinkable,
    error: linkableError,
  } = useLinkableClients(
    agencyId,
    {
      pageNumber: 1,
      pageSize: SEARCH_RESULT_LIMIT,
      search: debouncedSearch || undefined,
    },
    // Fetched on open, before any typing, so the dropdown is never empty.
    { enabled: pickerOpen },
  );

  // Rendered exactly as the server returned them. The endpoint already keeps
  // to marketing-enabled venues and already excludes the ones connected to
  // this agency, so a second pass here would only drop rows it meant to offer.
  const linkableClients = linkableData?.data ?? [];

  // Every opening reads a fresh list — clear what the last opening cached
  // before the query re-enables, so it refetches instead of repainting it.
  const clearLinkableClients = useClearLinkableClients(agencyId);
  const handlePickerOpenChange = (open: boolean) => {
    if (open) clearLinkableClients();
    setPickerOpen(open);
  };

  const connectMutation = useConnectAgencyClient(agencyId);
  const disconnectMutation = useDisconnectAgencyClient(agencyId);

  // Debounce the picker's input.
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  React.useEffect(() => {
    onTotalChange?.(totalClients);
  }, [totalClients, onTotalChange]);

  // Rows in flight are tracked against the page they were started on, so
  // changing page drops the tracking rather than mistaking "not on this page"
  // for "finished". Pressing delete again resumes a job that is still running.
  React.useEffect(() => {
    setRemoving({});
  }, [pageIndex]);

  // Removing the last client on a page strands the admin on a page that no
  // longer exists, with only Previous to get out. Step back for them. Guarded
  // on `clientsData` so the very first render, which has no count yet, does
  // not read as "this page is past the end".
  React.useEffect(() => {
    if (!clientsData) return;
    const lastPageIndex = Math.max(0, Math.ceil(totalClients / PAGE_SIZE) - 1);
    if (pageIndex > lastPageIndex) setPageIndex(lastPageIndex);
  }, [clientsData, totalClients, pageIndex]);

  /**
   * Reconcile what we are waiting for against what the server just returned.
   *
   * A row that has stopped coming back is DONE — the backend deletes the
   * connection row last, so its absence means the whole cascade finished. A
   * row still present past the ceiling has its control handed back.
   *
   * Keyed on `dataUpdatedAt` as well as the data itself: TanStack shares
   * structure between fetches, so an unchanged list arrives as the very same
   * object and would never re-run this — which is exactly the case where the
   * ceiling needs checking.
   *
   * `errorUpdatedAt` is in the deps for the same reason, and it is the one that
   * makes the ceiling reachable at all: a FAILING poll leaves `clientsData`
   * identical and never advances `dataUpdatedAt`, so without it nothing here
   * would re-run, `removing` would never drain, and the 3s `refetchInterval`
   * it gates would keep hitting a broken endpoint for the life of the page
   * with the row stuck on "Removing…".
   */
  React.useEffect(() => {
    const entries = Object.entries(removing);
    if (!clientsData || entries.length === 0) return;

    const present = new Set(clientsData.data.map((client) => client.id));
    const now = Date.now();

    const finished = entries.filter(([id]) => !present.has(id));
    const expired = entries.filter(
      ([id, removal]) =>
        present.has(id) && now - removal.startedAt > REMOVAL_TIMEOUT_MS,
    );

    if (finished.length === 0 && expired.length === 0) return;

    finished.forEach(([, removal]) => {
      toast.success(
        `"${removal.name}" is no longer a client of ${agencyName}.`,
      );
    });
    expired.forEach(([, removal]) => {
      toast.error(
        `Removing "${removal.name}" has not finished. Press Remove again to resume it.`,
      );
    });

    const settled = new Set([...finished, ...expired].map(([id]) => id));
    setRemoving((prev) =>
      Object.fromEntries(
        Object.entries(prev).filter(([id]) => !settled.has(id)),
      ),
    );
  }, [clientsData, dataUpdatedAt, errorUpdatedAt, removing, agencyName]);

  const handleSelectLinkableClient = (client: LinkableClient) => {
    setPendingConnect(client);
    setPickerOpen(false);
    setIsConnectDialogOpen(true);
  };

  const handleConnectConfirm = () => {
    if (!pendingConnect) return;
    const client = pendingConnect;

    connectMutation.mutate(
      { venueId: client.id },
      {
        onSuccess: () => {
          toast.success(`"${client.name}" is now a client of ${agencyName}.`);
          setIsConnectDialogOpen(false);
          setPendingConnect(null);
          // Clear the picker and return to the first page, which is where the
          // newly connected client is reachable from.
          setSearchQuery("");
          setDebouncedSearch("");
          setPageIndex(0);
        },
        onError: (error) => {
          // The server owns the refusal — unknown venue, marketing switched
          // off, already connected — so its sentence is what gets shown.
          toast.error(
            resolveAgencyClientError(
              error,
              `Couldn't connect "${client.name}" to ${agencyName}.`,
            ),
          );
          setIsConnectDialogOpen(false);
          setPendingConnect(null);
        },
      },
    );
  };

  const handleDisconnectConfirm = () => {
    if (!pendingDisconnect) return;
    const client = pendingDisconnect;

    disconnectMutation.mutate(client.id, {
      onSuccess: () => {
        // 202: ACCEPTED, NOT FINISHED. So this marks the row as being removed
        // and says the work is under way — it does NOT report a completed
        // removal. The row stays, with its delete control gone, until the
        // server stops returning it.
        setRemoving((prev) => ({
          ...prev,
          [client.id]: { startedAt: Date.now(), name: client.name },
        }));
        setIsDisconnectDialogOpen(false);
        setPendingDisconnect(null);
        toast.info(
          `Removing "${client.name}" from ${agencyName}. This runs in the background — the row disappears when it finishes.`,
        );
      },
      onError: (error) => {
        // The request was refused outright, so no job is running and the row
        // never enters the removing state.
        setIsDisconnectDialogOpen(false);
        setPendingDisconnect(null);
        toast.error(
          resolveAgencyClientError(
            error,
            `Couldn't remove "${client.name}" from ${agencyName}.`,
          ),
        );
      },
    });
  };

  const totalPages = Math.ceil(totalClients / PAGE_SIZE);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <CardTitle>Agency Clients</CardTitle>
            <div className="flex items-center gap-2">
              <Badge variant="secondary" className="font-semibold">
                {totalClients} Total
              </Badge>
            </div>
          </div>

          {/* This box DOES NOT FILTER THE TABLE BELOW — it searches every
              marketing-enabled venue not already connected to this agency. The
              placement invites exactly that misreading, so the trigger, the
              input's placeholder and the no-results copy all say what it
              actually does. */}
          <Popover open={pickerOpen} onOpenChange={handlePickerOpenChange}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                aria-expanded={pickerOpen}
                className="w-full max-w-xs justify-between font-normal text-muted-foreground"
              >
                <span className="truncate">
                  Search all venues to add a client
                </span>
                <IconSearch className="ml-2 size-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[22rem] p-0" align="end">
              {/* `shouldFilter={false}`: the match is the server's, on every
                  venue. Letting cmdk filter again would hide rows it offered. */}
              <Command shouldFilter={false}>
                <CommandInput
                  // Named explicitly: the placeholder is the only other name
                  // this input has, and it disappears as soon as the admin
                  // types.
                  aria-label="Search all venues to add a client"
                  placeholder="Search all venues to add a client..."
                  value={searchQuery}
                  onValueChange={setSearchQuery}
                />
                <CommandList>
                  {isLoadingLinkable ? (
                    <div
                      role="status"
                      className="flex items-center justify-center px-4 py-6"
                    >
                      <IconLoader2 className="mr-2 size-4 animate-spin" />
                      <span className="text-sm text-muted-foreground">
                        Searching venues...
                      </span>
                    </div>
                  ) : linkableError ? (
                    <p
                      role="alert"
                      className="px-4 py-6 text-center text-sm text-destructive"
                    >
                      {resolveAgencyClientError(
                        linkableError,
                        "Couldn't search venues.",
                      )}
                    </p>
                  ) : linkableClients.length === 0 ? (
                    <p className="px-4 py-6 text-center text-sm text-muted-foreground">
                      No venues found. This searches all venues to add a client,
                      not the list below — only venues with marketing enabled
                      that aren&apos;t already connected appear here.
                    </p>
                  ) : (
                    <CommandGroup>
                      {linkableClients.map((client) => (
                        <CommandItem
                          key={client.id}
                          value={client.id}
                          onSelect={() => handleSelectLinkableClient(client)}
                          className="gap-3"
                        >
                          <Avatar className="size-6">
                            {client.logo ? (
                              <AvatarImage src={client.logo} alt="" />
                            ) : null}
                            <AvatarFallback className="text-[0.625rem]">
                              {getInitials(client.name)}
                            </AvatarFallback>
                          </Avatar>
                          {/*
                            The name wins the width: the location's outsized
                            flex-shrink makes it absorb nearly all the overflow,
                            so a long address truncates (to nothing if need be)
                            before the name loses a character.
                          */}
                          <span className="min-w-0 truncate" title={client.name}>
                            {client.name}
                          </span>
                          {client.city || client.state ? (
                            <span
                              className="ml-auto min-w-0 shrink-[1000] truncate text-xs text-muted-foreground"
                              title={[client.city, client.state]
                                .filter(Boolean)
                                .join(", ")}
                            >
                              {[client.city, client.state]
                                .filter(Boolean)
                                .join(", ")}
                            </span>
                          ) : null}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  )}
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </div>
      </CardHeader>
      <CardContent>
        {clientsError ? (
          <div
            role="alert"
            className="rounded-lg border border-destructive/50 bg-destructive/10 p-4"
          >
            <p className="text-sm text-destructive">
              {resolveAgencyClientError(
                clientsError,
                "Failed to load this agency's clients.",
              )}
            </p>
          </div>
        ) : clients.length === 0 && !isLoadingClients && totalClients === 0 ? (
          <div className="flex flex-col items-center justify-center rounded-lg border border-dashed p-12">
            <IconBuildingStore className="mb-3 size-8 text-muted-foreground" />
            <p className="text-muted-foreground mb-1">
              No clients connected to this agency
            </p>
            <p className="text-sm text-muted-foreground">
              Use the search above to connect a venue.
            </p>
          </div>
        ) : (
          <>
            <div className="overflow-hidden rounded-lg border">
              <Table>
                <TableHeader className="bg-muted">
                  <TableRow>
                    <TableHead className="pl-6">Client</TableHead>
                    <TableHead>City</TableHead>
                    <TableHead>State</TableHead>
                    <TableHead>Connected</TableHead>
                    <TableHead className="pr-6 text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isLoadingClients ? (
                    <TableRow>
                      <TableCell colSpan={5} className="h-24 text-center">
                        <div
                          role="status"
                          className="flex items-center justify-center"
                        >
                          <IconLoader2 className="mr-2 size-4 animate-spin" />
                          <span className="text-muted-foreground">
                            Loading clients...
                          </span>
                        </div>
                      </TableCell>
                    </TableRow>
                  ) : clients.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={5}
                        className="h-24 text-center text-muted-foreground"
                      >
                        No clients on this page.
                      </TableCell>
                    </TableRow>
                  ) : (
                    clients.map((client) => {
                      const isRemoving = Boolean(removing[client.id]);

                      return (
                        <TableRow
                          key={client.id}
                          data-state={isRemoving ? "removing" : undefined}
                          className={isRemoving ? "opacity-60" : undefined}
                        >
                          <TableCell className="pl-6">
                            <div className="flex min-w-0 items-center gap-3">
                              <Avatar className="size-8 shrink-0">
                                {client.logo ? (
                                  <AvatarImage src={client.logo} alt="" />
                                ) : null}
                                <AvatarFallback className="text-xs">
                                  {getInitials(client.name)}
                                </AvatarFallback>
                              </Avatar>
                              <span
                                className="min-w-0 truncate font-medium"
                                title={client.name}
                              >
                                {client.name}
                              </span>
                            </div>
                          </TableCell>
                          <TableCell className="text-muted-foreground">
                            {displayOrDash(client.city)}
                          </TableCell>
                          <TableCell className="text-muted-foreground">
                            {displayOrDash(client.state)}
                          </TableCell>
                          <TableCell className="text-muted-foreground">
                            {formatDate(client.connectedAt)}
                          </TableCell>
                          <TableCell className="pr-6 text-right">
                            {isRemoving ? (
                              <span className="inline-flex items-center text-sm text-muted-foreground">
                                <IconLoader2 className="mr-2 size-4 animate-spin" />
                                Removing...
                              </span>
                            ) : (
                              <Button
                                variant="ghost"
                                size="icon"
                                className="size-8 text-destructive hover:bg-destructive/10 hover:text-destructive"
                                onClick={() => {
                                  setPendingDisconnect(client);
                                  setIsDisconnectDialogOpen(true);
                                }}
                              >
                                <IconTrash className="size-4" />
                                <span className="sr-only">
                                  Remove {client.name} as a client
                                </span>
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </div>

            {totalClients > 0 && (
              <div className="flex items-center justify-between px-2 mt-4">
                <div className="text-muted-foreground flex-1 text-sm">
                  Showing {clients.length > 0 ? pageIndex * PAGE_SIZE + 1 : 0}{" "}
                  to {Math.min((pageIndex + 1) * PAGE_SIZE, totalClients)} of{" "}
                  {totalClients} {totalClients === 1 ? "client" : "clients"}
                </div>
                <div className="flex items-center space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPageIndex((prev) => prev - 1)}
                    disabled={pageIndex === 0 || isLoadingClients}
                  >
                    Previous
                  </Button>
                  <div className="text-sm font-medium">
                    Page {pageIndex + 1} of {Math.max(1, totalPages)}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPageIndex((prev) => prev + 1)}
                    disabled={pageIndex >= totalPages - 1 || isLoadingClients}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </CardContent>

      {/* Both dialogs are embedded here, as the venues table embeds its own.
          They are separate components on purpose: the connect confirmation is
          routine, the disconnect one guards permanent loss, and the two must
          not be mistaken for each other. */}
      <ConnectAgencyClientDialog
        open={isConnectDialogOpen}
        onOpenChange={(open) => {
          setIsConnectDialogOpen(open);
          if (!open) setPendingConnect(null);
        }}
        onConfirm={handleConnectConfirm}
        agencyName={agencyName}
        clientName={pendingConnect?.name}
        isConnecting={connectMutation.isPending}
      />

      <DisconnectAgencyClientDialog
        open={isDisconnectDialogOpen}
        onOpenChange={(open) => {
          setIsDisconnectDialogOpen(open);
          if (!open) setPendingDisconnect(null);
        }}
        onConfirm={handleDisconnectConfirm}
        agencyName={agencyName}
        clientName={pendingDisconnect?.name}
        isDisconnecting={disconnectMutation.isPending}
      />
    </Card>
  );
}
